import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Undo2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  apiGetTimesheetWeek, apiGetTimekeepingTasks,
  apiApproveTimesheet, apiRejectTimesheet, apiReturnTimesheet,
} from "@/lib/api";
import { WeeklyGrid } from "@/components/timekeeping/WeeklyGrid";
import { buildHoursMap } from "@/components/timekeeping/hoursMap";
import { TimesheetStatusBadge } from "@/components/timekeeping/TimesheetStatusBadge";
import { formatLocalizedDate } from "@/lib/localized-date";
import { getTimekeepingErrorMessage, isTerminalReviewConflict } from "@/lib/timekeeping-errors";

export interface ReviewTarget {
  id: number;
  employee_id: number;
  employee_name: string;
  week_start_date: string;
}

interface Props {
  target: ReviewTarget | null;
  onOpenChange: (v: boolean) => void;
  onDone: () => void;
}

export function TimecardReviewDialog({ target, onOpenChange, onDone }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [action, setAction] = useState<"reject" | "return" | null>(null);
  const [reason, setReason] = useState("");

  const open = !!target;

  const { data: week, isLoading } = useQuery({
    queryKey: ["timecard-review", target?.employee_id, target?.week_start_date],
    queryFn: () => apiGetTimesheetWeek(target!.week_start_date, target!.employee_id),
    enabled: open,
  });
  const { data: tasks = [] } = useQuery({
    queryKey: ["timekeeping-tasks-all"],
    queryFn: () => apiGetTimekeepingTasks(true),
    enabled: open,
  });

  const finish = (updated: NonNullable<typeof week>, msg: string) => {
    qc.setQueryData(["timecard-review", target?.employee_id, target?.week_start_date], updated);
    qc.invalidateQueries({ queryKey: ["timesheet-approvals"] });
    qc.invalidateQueries({ queryKey: ["timecard-review"] });
    qc.invalidateQueries({ queryKey: ["timekeeping-report"] });
    qc.invalidateQueries({ queryKey: ["timesheet-week"] });
    qc.invalidateQueries({ queryKey: ["mobile-timesheet-week"] });
    qc.invalidateQueries({ queryKey: ["admin-timesheet-week"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["notificationCount"] });
    toast.success(msg);
    setAction(null); setReason("");
    onDone();
  };

  const handleActionError = (error: unknown) => {
    toast.error(getTimekeepingErrorMessage(error, t));
    if (isTerminalReviewConflict(error)) {
      qc.invalidateQueries({ queryKey: ["timesheet-approvals"] });
      qc.invalidateQueries({ queryKey: ["timecard-review"] });
      onDone();
    }
  };

  const approveMut = useMutation({
    mutationFn: () => apiApproveTimesheet(target!.id),
    onSuccess: (updated) => finish(
      updated,
      updated.status === "Approved"
        ? t("timekeeping.approvals.approved", { defaultValue: "Timesheet approved" })
        : t("timekeeping.approvals.reviewRecorded", { defaultValue: "Your approval was recorded" }),
    ),
    onError: handleActionError,
  });
  const reviewMut = useMutation({
    mutationFn: () => (action === "reject"
      ? apiRejectTimesheet(target!.id, reason)
      : apiReturnTimesheet(target!.id, reason)),
    onSuccess: (updated) => finish(updated, t("timekeeping.approvals.done", { defaultValue: "Done" })),
    onError: handleActionError,
  });

  const busy = approveMut.isPending || reviewMut.isPending;
  const language = i18n.resolvedLanguage || i18n.language;
  const effectiveRequiredHours = week && Number.isFinite(Number(week.effectiveRequiredHours))
    ? Number(week.effectiveRequiredHours)
    : week?.requiredHours;

  const notes = (week?.days ?? []).flatMap((d) =>
    d.entries.filter((e) => e.notes && e.notes.trim())
      .map((e) => ({ date: d.date, task: e.task_name ?? "—", note: e.notes as string })),
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setAction(null); setReason(""); } onOpenChange(v); }}>
      <DialogContent className="grid h-[min(52rem,calc(100vh-1rem))] w-[calc(100vw-1rem)] max-w-5xl grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:h-auto sm:max-h-[calc(100vh-2rem)] sm:w-[calc(100vw-2rem)] [&>button]:right-4 [&>button]:top-4 [&>button]:rounded-md [&>button]:p-1">
        <DialogHeader className="min-w-0 border-b px-4 py-4 pr-12 sm:px-6 sm:py-5 sm:pr-14">
          <DialogTitle className="flex min-w-0 items-center gap-3 pr-1">
            <span className="truncate">{target?.employee_name}</span>
            {week && <span className="shrink-0"><TimesheetStatusBadge status={week.status} /></span>}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-left">
            <span>{t("timekeeping.approvals.reviewDescription", { defaultValue: "Review the weekly entries, totals, and comments before taking action." })}</span>
            {week && (
              <>
                <span>{formatLocalizedDate(week.weekStartDate, language)} – {formatLocalizedDate(week.weekEndDate, language)}</span>
                <span className="font-medium tabular-nums text-foreground">
                  {week.totalHours} / {effectiveRequiredHours}h
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !week ? (
          <div className="min-h-0 p-4 sm:p-6"><Skeleton className="h-64 w-full" /></div>
        ) : (
          <div className="min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 sm:py-5">
            <div className="min-w-0 space-y-4">
              {((week.leaveDays ?? 0) > 0 || (week.holidayDays ?? 0) > 0
                || (week.scheduledRequiredHours ?? week.requiredHours) !== week.requiredHours) && (
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  {t("timekeeping.week.effectiveTargetExplanation", {
                    baseline: week.requiredHours,
                    effective: effectiveRequiredHours,
                    leaveDays: week.leaveDays ?? 0,
                    holidayDays: week.holidayDays ?? 0,
                    defaultValue: "Standard baseline: {{baseline}}h · Effective target: {{effective}}h (approved leave: {{leaveDays}}, public holidays: {{holidayDays}})",
                  })}
                </div>
              )}

              {/* Prior reject / return reason */}
              {week.status === "Rejected" && week.rejectReason && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400">
                  {t("timekeeping.status.rejected", { defaultValue: "Rejected" })}: {week.rejectReason}
                </div>
              )}
              {week.status === "Needs Changes" && week.returnReason && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                  {t("timekeeping.status.needsChanges", { defaultValue: "Needs changes" })}: {week.returnReason}
                </div>
              )}

              {/* Read-only weekly grid */}
              <WeeklyGrid week={week} tasks={tasks} editable={false} value={buildHoursMap(week)} onChange={() => {}} />

              {/* Comments / notes */}
              <div>
                <div className="text-sm font-medium mb-1">{t("timekeeping.approvals.notes", { defaultValue: "Comments" })}</div>
                {notes.length === 0 ? (
                  <div className="text-sm text-muted-foreground">{t("timekeeping.approvals.noNotes", { defaultValue: "No comments on this timesheet." })}</div>
                ) : (
                  <ul className="space-y-1">
                    {notes.map((n, i) => (
                      <li key={i} className="flex min-w-0 flex-wrap gap-2 text-sm sm:flex-nowrap">
                        <span className="text-muted-foreground tabular-nums shrink-0">
                          {formatLocalizedDate(n.date, language)}
                        </span>
                        <Badge variant="secondary" className="shrink-0">{n.task}</Badge>
                        <span className="min-w-0 break-words">{n.note}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {!isLoading && week?.status === "Submitted" ? (
          <div className="border-t bg-background px-4 py-3 sm:px-6 sm:py-4">
            {action && (
              <Textarea
                autoFocus
                placeholder={t("timekeeping.approvals.reasonPlaceholder", { defaultValue: "Reason shown to the employee" })}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                className="mb-3 max-h-28 min-h-[64px]"
              />
            )}
            <div className="flex flex-wrap items-center justify-end gap-2">
                {action ? (
                  <>
                    <Button variant="outline" disabled={busy} onClick={() => { setAction(null); setReason(""); }}>
                      {t("common.cancel", { defaultValue: "Cancel" })}
                    </Button>
                    <Button disabled={busy} onClick={() => reviewMut.mutate()}>
                      {action === "reject"
                        ? t("timekeeping.approvals.rejectTitle", { defaultValue: "Reject timesheet" })
                        : t("timekeeping.approvals.returnTitle", { defaultValue: "Return for changes" })}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" disabled={busy} onClick={() => { setReason(""); setAction("reject"); }}>
                      <XCircle className="h-4 w-4 mr-1 text-destructive" />{t("timekeeping.approvals.reject", { defaultValue: "Reject" })}
                    </Button>
                    <Button variant="outline" disabled={busy} onClick={() => { setReason(""); setAction("return"); }}>
                      <Undo2 className="h-4 w-4 mr-1" />{t("timekeeping.approvals.return", { defaultValue: "Return for changes" })}
                    </Button>
                    <Button className="text-emerald-600" variant="outline" disabled={busy} onClick={() => approveMut.mutate()}>
                      <CheckCircle2 className="h-4 w-4 mr-1" />{t("timekeeping.approvals.approve", { defaultValue: "Approve" })}
                    </Button>
                  </>
                )}
            </div>
          </div>
        ) : <div />}
      </DialogContent>
    </Dialog>
  );
}
